"use client"

import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { platformStorage } from '@/lib/platform-storage'
import { convex, whenConvexReady } from '@/lib/convex-client'
import { api } from "@/convex/_generated/api"
import { useAuthStore } from './auth-store'

interface PreferencesState {
  // State
  defaultDuration: number
  autoSchedule: boolean
  autoStart: boolean
  isLoading: boolean
  
  // Actions
  refreshPreferences: () => Promise<void>
  updatePreferences: (updates: Partial<{
    defaultDuration: number
    autoSchedule: boolean
    autoStart: boolean
  }>) => Promise<void>
  
  // Internal setters
  setDefaultDuration: (duration: number) => void
  setAutoSchedule: (autoSchedule: boolean) => void
  setAutoStart: (autoStart: boolean) => void
  setIsLoading: (loading: boolean) => void
}

export const usePreferencesStore = create<PreferencesState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        defaultDuration: 30,
        autoSchedule: false,
        autoStart: false,
        isLoading: true,

        // Internal setters
        setDefaultDuration: (defaultDuration) => set({ defaultDuration }),
        setAutoSchedule: (autoSchedule) => set({ autoSchedule }),
        setAutoStart: (autoStart) => set({ autoStart }),
        setIsLoading: (isLoading) => set({ isLoading }),

        refreshPreferences: async () => {
          const user = useAuthStore.getState().user
          if (!user) {
            set({ isLoading: false })
            return
          }

          try {
            // Preferences live on the Convex user document (the schema merges
            // the old profiles + user_preferences tables), so the missing-row
            // and missing-table handling below is no longer needed.
            await whenConvexReady()
            const data = await convex.query(api.users.current, {})

            if (data) {
              set({
                defaultDuration: data.defaultDuration ?? 30,
                autoSchedule: data.autoSchedule ?? false,
                autoStart: data.autoStart ?? false
              })
            }
          } catch (error: unknown) {
            console.error("Error fetching preferences:", error instanceof Error ? error.message : error)
          } finally {
            set({ isLoading: false })
          }
        },

        updatePreferences: async (updates) => {
          const user = useAuthStore.getState().user
          if (!user) throw new Error("User not authenticated")

          try {
            await whenConvexReady()
            await convex.mutation(api.users.setPreferences, {
              defaultDuration: updates.defaultDuration ?? get().defaultDuration,
              autoSchedule: updates.autoSchedule ?? get().autoSchedule,
              autoStart: updates.autoStart ?? get().autoStart,
            })

            // Update local state
            set(updates)
          } catch (error) {
            console.error("Error updating preferences:", error)
            throw error
          }
        }
      }),
      {
        name: 'preferences-storage',
        // Named rather than defaulted: zustand falls back to `localStorage`,
        // which does not exist on a phone. Metro resolves the `.native`
        // sibling to AsyncStorage so this store file runs on both clients.
        storage: createJSONStorage(() => platformStorage),
        partialize: (state) => ({
          defaultDuration: state.defaultDuration,
          autoSchedule: state.autoSchedule,
          autoStart: state.autoStart
        })
      }
    ),
    { name: 'preferences-store' }
  )
)
